import { ApiService } from "../../insights-agent/api/ApiService.interface.ts";
import { PollingService } from "../../insights-agent/api/PollingService/PollingService.ts";
import { AuthService } from "../../auth/AuthService.ts";
import { HttpClient } from "../../http/HttpClient.ts";
import { Subscribable } from "../../Subscribable.ts";
import { AppState, FilterState, Message, ResearchId } from "./AppState.ts";
import {
  ProductEvent,
  ProductEventTarget,
} from "../../product-events/ProductEventTarget.ts";
import { createMessagePollingManager } from "./messagePolling.ts";

function getAppContext(
  enableClaMode: boolean,
  enableProductTestingBusinessDomain: boolean,
): "insights_agent" | "cla" | "pla" {
  if (enableClaMode && enableProductTestingBusinessDomain) {
    return "pla";
  }
  if (enableClaMode) {
    return "cla";
  }
  return "insights_agent";
}

export class DeepResearchService extends Subscribable<AppState> {
  private readonly enableClaMode: boolean;
  private readonly enableProductTestingBusinessDomain: boolean;

  mergeMessageContent(
    message: Message,
    addContent: string,
    thought: boolean,
  ): void {
    if (thought) {
      message.thoughts = (message.thoughts ?? "") + addContent;
    } else {
      message.value = (message.value ?? "") + addContent;
    }
  }

  /**
   * Merges a block of consecutive agent messages into a single message.
   * Messages with thought=true have their value merged into `thoughts`.
   * Messages with thought=false have their value merged into `value`.
   *
   * @param messages - Array of agent messages to merge (must not be empty)
   * @returns A single merged Message
   */
  mergeAgentMessageBlock(messages: Message[]): Message {
    const merged: Message = {
      id: messages[0].id,
      value: "",
      thoughts: "",
      sender: "agent",
      create_time: messages[0].create_time,
      status: "completed",
      citations: [],
      references: [],
      actions: [],
    };

    messages.forEach((msg) => {
      this.mergeMessageContent(merged, msg.value ?? "", msg.thought ?? false);

      if (msg.citations) {
        merged.citations = [...(merged.citations ?? []), ...msg.citations];
      }
      if (msg.references) {
        merged.references = [...(merged.references ?? []), ...msg.references];
      }
      if (msg.actions) {
        merged.actions = [...(merged.actions ?? []), ...msg.actions];
      }
      if (msg.appliedFilters) {
        merged.appliedFilters = msg.appliedFilters;
      }
    });

    return merged;
  }

  constructor(
    private readonly httpClient: HttpClient,
    readonly authService: AuthService,
    private readonly apiService: ApiService,
    private readonly pollingService: PollingService,
    private readonly productEvents: ProductEventTarget,
    config: {
      enableClaMode: boolean;
      enableProductTestingBusinessDomain: boolean;
    },
  ) {
    super({
      authState: "loading",
      messageBoxSending: false,
      researchMap: {
        items: {},
        loading: true,
      },
      messages: {
        items: {},
        loading: true,
      },
      fileReferences: {
        items: {},
        loading: true,
      },
      filters: {
        items: {},
        loading: true,
      },
      filterOptions: {
        loading: true,
      },
      knowledgeSources: {
        items: [],
        loading: true,
      },
      researchTypes: {
        items: [],
        loading: true,
      },
      termsAndConditions: {
        status: "loading",
        content: null,
        loading: false,
        accepting: false,
        error: null,
      },
    });
    this.enableClaMode = config.enableClaMode;
    this.enableProductTestingBusinessDomain =
      config.enableProductTestingBusinessDomain;
    this.authService.subscribe(() => {
      this.updateState((draft) => {
        draft.authState = this.authService.getState().authState;
      });
      this.httpClient.setToken(this.authService.getState().token || null);
    });
    this.pollingService
      .startPolling(
        () => this.refreshResearches(),
        10000,
        async () =>
          Object.values(this.getState().researchMap.items).some(
            (research) => research.status === "processing_meta_analysis",
          ),
      )
      .then();
  }

  /**
   * Starts a new meta-analysis by calling the appropriate endpoint.
   *
   * Documentation:
   * https://backstage.dev.ipsosfacto.com/catalog/default/api/vertex-knowledge-source-answers-api-api/definition#/default/start_meta_analysis_knowledge_source_search_and_answers_research__post
   */
  async startMetaAnalysis(payload: {
    knowledgeSourceId: string;
    researchGoal: string;
    additionalQuestions: string;
    researchType?: string;
  }) {
    const result = await this.apiService.createResearch({
      knowledgeSourceId: payload.knowledgeSourceId,
      researchGoal: payload.researchGoal,
      additionalQuestions: payload.additionalQuestions,
      researchType: payload.researchType,
    });

    this.productEvents.dispatchEvent(new ProductEvent("chat_start"));

    // Wait for the research to be properly loaded before returning
    await this.refreshResearches();

    return result;
  }

  async startChatFirstResearch(input: {
    message: string;
    knowledgeSourceId?: string;
    filters?: FilterState;
  }): Promise<{ success: boolean; data?: { id: string } }> {
    return new Promise((resolve, reject) => {
      const requestData: {
        message: string;
        knowledge_source: string;
        filters?: FilterState;
      } = {
        message: input.message,
        knowledge_source: input.knowledgeSourceId || "",
      };

      if (input.filters && Object.keys(input.filters).length > 0) {
        requestData.filters = input.filters;
      }

      let resolved = false;
      let researchId: string | undefined;

      const pollingManager = createMessagePollingManager(
        this.apiService,
        this.updateState.bind(this),
        this.mergeMessageContent.bind(this),
      );

      this.httpClient.streamWithEventSource(
        "/research/chat-first",
        requestData,
        (sseEvent) => {
          const sseEventData = sseEvent.data as {
            research_id?: string;
            message_id?: string;
          };

          if (!resolved && sseEventData?.research_id) {
            researchId = sseEventData.research_id;
            resolved = true;

            this.updateState((draft) => {
              draft.messageBoxSending = true;
              if (!draft.messages.items[researchId as ResearchId]) {
                draft.messages.items[researchId as ResearchId] = [];
              }
              const messages = draft.messages.items[researchId as ResearchId];
              messages.push({
                id: "optimistic-user-msg-" + Date.now(),
                value: input.message,
                sender: "user",
                create_time: new Date().toISOString(),
                status: "completed",
              });
              messages.push({
                id: "optimistic-agent-msg",
                value: "",
                sender: "agent",
                create_time: new Date().toISOString(),
                status: "processing",
              });
            });

            this.productEvents.dispatchEvent(new ProductEvent("chat_start"));
            this.refreshResearches();
            resolve({ success: true, data: { id: researchId } });
          }

          if (researchId && sseEventData?.message_id) {
            pollingManager.startPollingForMessage(
              sseEventData.message_id,
              researchId as ResearchId,
            );
          }
        },
        async () => {
          if (!resolved) {
            resolve({ success: false, data: undefined });
          }
          if (researchId) {
            pollingManager.stopAllPolling();

            await this.refreshResearchItem({ id: researchId as ResearchId });
            await Promise.all([
              this.refreshMessages({
                researchId: researchId as ResearchId,
                useInitialQueryFallback: true,
              }),
              this.refreshFileReferences({
                researchId: researchId as ResearchId,
              }),
            ]);

            this.updateState((draft) => {
              draft.messageBoxSending = false;
            });
          }
        },
        (error) => {
          pollingManager.stopAllPolling();
          if (!resolved) {
            reject(error);
          }
          if (researchId) {
            this.refreshMessages({
              researchId: researchId as ResearchId,
              useInitialQueryFallback: true,
            }).finally(() => {
              this.updateState((draft) => {
                draft.messageBoxSending = false;
              });
            });
          }
        },
      );
    });
  }

  async refreshResearchTypes() {
    this.updateState((draft) => {
      draft.researchTypes.loading = true;
    });
    const { data } = await this.apiService.getResearchTypes();
    if (!data) {
      console.log("Error fetching research types");
      this.updateState((draft) => {
        draft.researchTypes.loading = false;
      });
      return;
    }
    this.updateState((draft) => {
      draft.researchTypes.items = data;
      draft.researchTypes.loading = false;
    });
  }

  async refreshResearches() {
    const { data } = await this.apiService.getResearchList();
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      draft.researchMap.items = {};
      data.researches.forEach((research) => {
        draft.researchMap.items[research.id] = research;
      });
      draft.researchMap.loading = false;
    });
  }

  async refreshResearchItem(payload: { id: ResearchId }) {
    const { data, success } = await this.apiService.getResearch({
      id: payload.id,
    });
    if (!success) {
      console.log(`Error fetching research ${payload.id}`);
      window.location.href = "/";
      return;
    }
    if (!data) return;
    this.updateState((draft) => {
      draft.researchMap.items[data.id] = data;
      draft.researchMap.loading = false;
    });
  }

  async sendMessage(payload: { researchId: ResearchId; message: string }) {
    this.productEvents.dispatchEvent(new ProductEvent("followup_message_send"));
    this.updateState((draft) => {
      const messages = this.pickResearchMessages(draft, payload);
      messages.push({
        id: "optimistic_message",
        status: "optimistic",
        value: payload.message,
        sender: "user",
        create_time: new Date().toISOString(),
      });
      messages.push({
        id: "pending_message",
        status: "optimistic",
        sender: "agent",
        create_time: new Date().toISOString(),
      });
    });
    await this.apiService.submitMessage(payload);
    await this.refreshMessages({
      researchId: payload.researchId,
    });
  }

  /**
   * Sends a message using streaming (Server-Sent Events),
   * then polls getMessage for each message_id to get full results in real-time.
   */
  async sendMessageStreaming(i: {
    researchId: ResearchId;
    message: string;
    skipEventLogging?: boolean;
  }): Promise<string | void> {
    if (!i.skipEventLogging) {
      this.productEvents.dispatchEvent(
        new ProductEvent("followup_message_send"),
      );
    }

    this.updateState((draft) => {
      draft.messageBoxSending = true;
      const messages = this.pickResearchMessages(draft, i);
      messages.push({
        id: "optimistic-user-msg-" + Date.now(),
        value: i.message,
        sender: "user",
        create_time: new Date().toISOString(),
        status: "completed",
      });
      messages.push({
        id: "optimistic-agent-msg",
        value: "",
        sender: "agent",
        create_time: new Date().toISOString(),
        status: "processing",
      });
    });

    const pollingManager = createMessagePollingManager(
      this.apiService,
      this.updateState.bind(this),
      this.mergeMessageContent.bind(this),
    );

    this.httpClient
      .streamWithEventSource(
        `/research/${i.researchId}/messages/streaming/`,
        { query: i.message },
        (event) => {
          const eventData = event.data as { message_id?: string };
          if (eventData.message_id) {
            pollingManager.startPollingForMessage(
              eventData.message_id,
              i.researchId,
            );
          }
        },
        async () => {
          pollingManager.stopAllPolling();

          await Promise.all([
            this.refreshMessages({ researchId: i.researchId }),
            this.refreshResearchItem({ id: i.researchId }),
            this.refreshFileReferences({ researchId: i.researchId }),
          ]);

          this.updateState((draft) => {
            draft.messageBoxSending = false;
          });
        },
        (err) => {
          pollingManager.stopAllPolling();
          this.productEvents.dispatchEvent(
            new ProductEvent("exception", {
              description: "Stream error during message sending",
              fatal: false,
            }),
          );
          this.refreshMessages({ researchId: i.researchId }).then(() => {
            this.updateState((draft) => {
              draft.messageBoxSending = false;
            });
          });
          throw err;
        },
      )
      .catch(() => {
        pollingManager.stopAllPolling();
        this.productEvents.dispatchEvent(
          new ProductEvent("exception", {
            description: "Fatal error during message sending",
            fatal: true,
          }),
        );
        this.refreshMessages({ researchId: i.researchId }).finally(() => {
          this.updateState((draft) => {
            draft.messageBoxSending = false;
          });
        });
      });
  }

  /**
   * Resends the last user query using streaming.
   * Ported from ClaService.
   */
  async resendLastQueryStreaming(i: {
    researchId: ResearchId;
  }): Promise<string | void> {
    const lastUserMessage = this.getLastMessageFromResearch(
      i.researchId,
      "user",
    );

    if (!lastUserMessage) {
      this.productEvents.dispatchEvent(
        new ProductEvent("exception", {
          description: "No previous user message found to resend.",
          fatal: false,
        }),
      );
      throw new Error("No previous user message found to resend.");
    }

    this.productEvents.dispatchEvent(new ProductEvent("message_retry"));

    return this.sendMessageStreaming({
      researchId: i.researchId,
      message: lastUserMessage.value || "",
    });
  }

  /**
   * Gets the last message from a research, optionally filtered by sender.
   * Ported from ClaService.
   */
  getLastMessageFromResearch(
    researchId: ResearchId,
    sender?: Message["sender"],
  ): Message | undefined {
    const messages = this.getState().messages.items[researchId];
    if (!messages || messages.length === 0) {
      return undefined;
    }
    return messages.findLast((msg) => (sender ? msg.sender === sender : true));
  }

  async refreshMessages(payload: {
    researchId: ResearchId;
    useInitialQueryFallback?: boolean;
  }) {
    this.updateState((draft) => {
      draft.messages.loading = true;
    });
    const { data, httpStatus } =
      await this.apiService.getResearchMessages(payload);
    if (!data) {
      console.log(
        `Error fetching messages for research ${payload.researchId}`,
        httpStatus,
      );
      this.updateState((draft) => {
        draft.messages.loading = false;
      });
      return;
    }
    this.updateState((draft) => {
      if (!draft.messages.items[payload.researchId]) {
        draft.messages.items[payload.researchId] = [];
      }

      const existingMessages = draft.messages.items[payload.researchId].filter(
        (msg) =>
          !msg.id.startsWith("optimistic-user-msg-") &&
          msg.id !== "streaming-agent-msg" &&
          msg.id !== "optimistic-agent-msg",
      );
      const newMessages = data.messages;

      newMessages.forEach((newMessage) => {
        const existingMessage = existingMessages.find(
          (m) => m.id === newMessage.id,
        );
        if (!existingMessage) {
          existingMessages.push(newMessage);
        } else {
          existingMessage.status = newMessage.status;
          existingMessage.value = newMessage.value;
          existingMessage.citations = newMessage.citations;
          existingMessage.references = newMessage.references;
          existingMessage.actions = newMessage.actions;
          existingMessage.appliedFilters = newMessage.appliedFilters;
        }
      });

      const completedMessages = existingMessages.filter(
        (msg) => msg.status === "completed",
      );

      if (payload.useInitialQueryFallback) {
        const briefId = `brief-${payload.researchId}`;
        const hasBriefMessage = completedMessages.some(
          (msg) => msg.id === briefId,
        );
        if (!hasBriefMessage) {
          const research = draft.researchMap.items[payload.researchId];
          if (research?.brief) {
            completedMessages.unshift({
              id: briefId,
              value: research.brief,
              sender: "user",
              create_time: research.create_time || new Date().toISOString(),
              status: "completed",
            });
          }
        }
      }

      draft.messages.items[payload.researchId] = completedMessages;
      draft.messages.loading = false;
    });
  }

  async refreshFileReferences(payload: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.fileReferences.loading = true;
    });
    const { data } = await this.apiService.getReferences(payload);
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      if (!draft.fileReferences.items[payload.researchId]) {
        draft.fileReferences.items[payload.researchId] = [];
      }

      draft.fileReferences.items[payload.researchId] = data.references.filter(
        (reference, index, self) =>
          index ===
          self.findIndex(
            (r) =>
              r.title === reference.title &&
              r.sourceUrl === reference.sourceUrl,
          ),
      );
      draft.fileReferences.loading = false;
    });
  }

  async removeReferences(payload: {
    researchId: ResearchId;
    referenceIds: string[];
  }) {
    this.productEvents.dispatchEvent(new ProductEvent("reference_remove"));
    await this.apiService.removeReferences(payload);
    await Promise.all([
      this.refreshFileReferences({ researchId: payload.researchId }),
      this.refreshMessages({ researchId: payload.researchId }),
    ]);
  }

  async refreshFilters(payload: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.filters.loading = true;
    });
    const { data, success } = await this.apiService.getFilters({
      researchId: payload.researchId,
    });
    if (!success) {
      console.log(`Error fetching filters for research ${payload.researchId}`);
      return;
    }
    if (!data) throw new Error("No data");
    this.updateState((draft) => {
      draft.filters.items[payload.researchId] = data;
      draft.filters.loading = false;
    });
  }

  async refreshFilterOptions(payload: {
    researchId: ResearchId;
  }): Promise<void> {
    this.updateState((draft) => {
      draft.filterOptions.loading = true;
      delete draft.filterOptions.items;
    });
    const { data: dataOptions } = await this.apiService.getFilterOptions({
      researchId: payload.researchId,
    });
    if (!dataOptions) throw new Error("No data");
    this.updateState((draft) => {
      if (dataOptions.filterOptionsList) {
        delete draft.filterOptions.items;
        draft.filterOptions.list = dataOptions.filterOptionsList || [];
      } else {
        delete draft.filterOptions.list;
        draft.filterOptions.items = dataOptions.filterOptions;
      }
      draft.filterOptions.loading = false;
    });
  }
  async refreshKnowledgeSources() {
    this.updateState((draft) => {
      draft.knowledgeSources.loading = true;
    });
    const { data } = await this.apiService.getKnowledgeSources();
    this.updateState((draft) => {
      draft.knowledgeSources.loading = false;
      if (!data) return;
      draft.knowledgeSources.items = data.knowledgeSources;
    });
  }

  async getFiltersByKnowledgeSource(payload: {
    knowledgeSourceName: string;
  }): Promise<void> {
    this.updateState((draft) => {
      draft.filterOptions.loading = true;
      delete draft.filterOptions.items;
      delete draft.filterOptions.list;
    });

    const result = await this.apiService.getFiltersByKnowledgeSource(payload);

    this.updateState((draft) => {
      draft.filterOptions.loading = false;
      if (result.success && result.data?.filters) {
        draft.filterOptions.list = result.data.filters;
      }
    });
  }

  async setFilters(param: { researchId: ResearchId; filters: FilterState }) {
    this.productEvents.dispatchEvent(new ProductEvent("filter_change"));
    this.updateState((draft) => {
      draft.filters.loading = true;
      draft.filters.items[param.researchId] = param.filters;
    });
    await this.apiService.setFilters(param);
    await this.refreshFilters({
      researchId: param.researchId,
    });
  }

  async regenerateSummary(param: { researchId: ResearchId }) {
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.regenerating_summary = true;
      const messages = this.pickResearchMessages(draft, param);
      messages.push({
        id: "optimistic_summary_request",
        status: "optimistic",
        value: "Regenerate summary.",
        sender: "user",
        create_time: new Date().toISOString(),
      });
      messages.push({
        id: "pending_summary_response",
        status: "optimistic",
        sender: "agent",
        create_time: new Date().toISOString(),
      });
    });
    await this.apiService.regenerateSummary(param);
    await this.refreshMessages({ researchId: param.researchId });
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.regenerating_summary = false;
    });
  }

  async deleteResearch(param: { researchId: ResearchId }) {
    this.updateState((draft) => {
      draft.researchMap.loading = true;
      delete draft.researchMap.items[param.researchId];
    });
    await this.apiService.deleteResearch(param);
    await this.refreshResearches();
  }

  async renameResearch(param: { researchId: ResearchId; name: string }) {
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research) return;
      research.display_name = param.name;
      research.update_time = new Date().toISOString();
    });
    const result = await this.apiService.renameResearch({
      researchId: param.researchId,
      name: param.name,
    });
    this.updateState((draft) => {
      const research = draft.researchMap.items[param.researchId];
      if (!research || !result.data) return;
      research.display_name = result.data?.research.display_name || "";
      research.update_time = result.data.research.update_time;
    });
  }

  async refreshTermsAndConditions(input?: {
    forceContentLoad?: boolean;
  }): Promise<void> {
    this.updateState((draft) => {
      draft.termsAndConditions.loading = true;
      draft.termsAndConditions.error = null;
    });
    try {
      // Get status first
      const statusResult = await this.apiService.getTermsAndConditionsStatus({
        appContext: getAppContext(
          this.enableClaMode,
          this.enableProductTestingBusinessDomain,
        ),
      });

      if (statusResult.success && statusResult.data) {
        const status = statusResult.data.status;
        this.updateState((draft) => {
          draft.termsAndConditions.status = status;
        });

        // If terms are pending, also fetch the content
        // If forceContentLoad is true, fetch the content regardless of status
        if (status === "pending_acceptance" || input?.forceContentLoad) {
          const contentResult =
            await this.apiService.getLatestTermsAndConditions({
              appContext: getAppContext(
                this.enableClaMode,
                this.enableProductTestingBusinessDomain,
              ),
            });
          if (contentResult.success && contentResult.data) {
            const content = contentResult.data.content;
            this.updateState((draft) => {
              draft.termsAndConditions.content = content;
            });
          }
        }
      } else {
        this.updateState((draft) => {
          draft.termsAndConditions.status = "error";
          draft.termsAndConditions.error =
            "Failed to load terms and conditions. Please try again.";
        });
      }
    } catch (error) {
      console.log("Error fetching terms and conditions", error);
      this.updateState((draft) => {
        draft.termsAndConditions.status = "error";
        draft.termsAndConditions.error =
          "Failed to load terms and conditions. Please try again.";
      });
    } finally {
      this.updateState((draft) => {
        draft.termsAndConditions.loading = false;
      });
    }
  }

  async acceptTermsAndConditions(): Promise<void> {
    this.updateState((draft) => {
      draft.termsAndConditions.accepting = true;
    });
    const failedToAccept = (error?: Error) => {
      this.updateState((draft) => {
        draft.termsAndConditions.status = "error";
        draft.termsAndConditions.error =
          "Failed to accept terms and conditions. Please try again later.";
        console.error("Failed to accept terms and conditions", error);
      });
    };
    try {
      const result = await this.apiService.acceptTermsAndConditions({
        appContext: getAppContext(
          this.enableClaMode,
          this.enableProductTestingBusinessDomain,
        ),
      });
      if (result.success) {
        this.updateState((draft) => {
          draft.termsAndConditions.status = "accepted";
        });
      } else {
        failedToAccept();
      }
    } catch (error) {
      failedToAccept(error as Error);
    } finally {
      this.updateState((draft) => {
        draft.termsAndConditions.accepting = false;
      });
    }
  }

  private pickResearchMessages(
    appState: AppState,
    payload: { researchId: ResearchId },
  ): Array<Message> {
    let messages = appState.messages.items[payload.researchId];
    if (!messages) messages = appState.messages.items[payload.researchId] = [];
    return messages;
  }
}
